/**
 * Security utilities and configurations
 */

/**
 * Security configuration for the application
 */
export const SECURITY_CONFIG = {
  // Content Security Policy configuration
  CSP: {
    'default-src': "'self'",
    'script-src': "'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com https://connect.facebook.net",
    'style-src': "'self' 'unsafe-inline'",
    'img-src': "'self' data: https: blob:",
    'connect-src': "'self' https://api.cassette.diy https://www.google-analytics.com https://api.logtail.com https://www.googletagmanager.com https://connect.facebook.net",
    'font-src': "'self' data:",
    'object-src': "'none'",
    'base-uri': "'self'",
    'form-action': "'self'",
    'frame-ancestors': "'none'",
  },
  
  // Allowed file types for uploads
  ALLOWED_FILE_TYPES: {
    audio: ['audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/mp3', 'audio/aac', 'audio/flac', 'audio/mp4', 'audio/ogg', 'application/zip', 'application/x-zip-compressed', 'application/x-rar-compressed', 'application/x-7z-compressed', 'application/vnd.rar'],
    video: ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/mov', 'video/avi', 'video/mkv'],
    image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  },
  
  // File extension fallback (for cases where MIME type is unreliable)
  ALLOWED_FILE_EXTENSIONS: {
    audio: ['.mp3', '.wav', '.aac', '.flac', '.m4a', '.ogg', '.zip', '.rar', '.7z'],
    video: ['.mp4', '.mov', '.avi', '.mkv'],
    image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  },
  
  // Maximum file sizes (in bytes)
  MAX_FILE_SIZES: {
    audio: 2 * 1024 * 1024 * 1024, // 2GB
    video: 2 * 1024 * 1024 * 1024, // 2GB
    image: 50 * 1024 * 1024, // 50MB
  },
  
  // Input validation limits
  INPUT_LIMITS: {
    searchQuery: 100,
    trackName: 200,
    userContent: 500,
    email: 254,
  },
  
  // Session configuration
  SESSION: {
    timeout: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
    refreshThreshold: 5 * 60 * 1000, // 5 minutes before expiry
  },
};

export type UploadFileCategory = keyof typeof SECURITY_CONFIG.ALLOWED_FILE_TYPES;

/** Native `<input accept>` and react-dropzone filters per upload context. */
export const UPLOAD_FILE_ACCEPT: Record<
  UploadFileCategory,
  { native: string; dropzone: Record<string, string[]>; hint: string }
> = {
  audio: {
    native: '.mp3,.wav,.aac,.flac,.m4a,.ogg,.zip,.rar,.7z',
    dropzone: {
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/x-wav': ['.wav'],
      'audio/wave': ['.wav'],
      'audio/aac': ['.aac'],
      'audio/flac': ['.flac'],
      'audio/mp4': ['.m4a'],
      'audio/ogg': ['.ogg'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'application/x-rar-compressed': ['.rar'],
      'application/vnd.rar': ['.rar'],
      'application/x-7z-compressed': ['.7z'],
    },
    hint: 'MP3, WAV, AAC, FLAC, or ZIP/RAR/7Z archives',
  },
  video: {
    native: '.mp4,.mov,.avi,.mkv',
    dropzone: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
      'video/x-matroska': ['.mkv'],
    },
    hint: 'MP4, MOV, AVI, or MKV',
  },
  image: {
    native: '.jpg,.jpeg,.png,.gif,.webp',
    dropzone: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    hint: 'JPG, PNG, GIF, or WebP',
  },
};

export function resolveUploadFileCategory(
  fileCategory?: UploadFileCategory,
  accept?: string,
): UploadFileCategory {
  if (fileCategory && UPLOAD_FILE_ACCEPT[fileCategory]) {
    return fileCategory;
  }
  const normalized = (accept ?? '').toLowerCase();
  if (normalized.includes('video')) return 'video';
  if (normalized.includes('image')) return 'image';
  if (
    normalized.includes('audio') ||
    normalized.includes('.mp3') ||
    normalized.includes('.wav') ||
    normalized.includes('.zip') ||
    normalized.includes('.rar') ||
    normalized.includes('.7z')
  ) {
    return 'audio';
  }
  return 'audio';
}

export function getUploadAcceptConfig(
  fileCategory?: UploadFileCategory,
  accept?: string,
): (typeof UPLOAD_FILE_ACCEPT)[UploadFileCategory] {
  const category = resolveUploadFileCategory(fileCategory, accept);
  return UPLOAD_FILE_ACCEPT[category] ?? UPLOAD_FILE_ACCEPT.audio;
}

export function filterFilesForCategory(
  files: File[],
  category: UploadFileCategory,
): { validFiles: File[]; errors: string[] } {
  const validFiles: File[] = [];
  const errors: string[] = [];

  files.forEach((file, index) => {
    if (!validateFileType(file, category)) {
      errors.push(`File ${index + 1}: ${file.name} is not a supported ${category} file`);
      return;
    }
    if (!validateFileSize(file, category)) {
      const maxMb = Math.round(SECURITY_CONFIG.MAX_FILE_SIZES[category] / (1024 * 1024));
      errors.push(`File ${index + 1}: ${file.name} is too large (max ${maxMb}MB)`);
      return;
    }
    validFiles.push(file);
  });

  return { validFiles, errors };
}

/**
 * Validates file type against allowed types
 * @param file - The file to validate
 * @param category - The file category (audio, video, image)
 * @returns True if file type is allowed
 */
export const validateFileType = (file: File, category: keyof typeof SECURITY_CONFIG.ALLOWED_FILE_TYPES): boolean => {
  // First, check MIME type
  if (file.type && SECURITY_CONFIG.ALLOWED_FILE_TYPES[category].includes(file.type)) {
    return true;
  }
  
  // Fallback: check file extension if MIME type is missing or not recognized
  // This handles cases where browsers report empty string or non-standard MIME types for WAV files
  const fileName = file.name.toLowerCase();
  const extensions = SECURITY_CONFIG.ALLOWED_FILE_EXTENSIONS[category];
  return extensions.some(ext => fileName.endsWith(ext));
};

/**
 * Validates file size against maximum allowed size
 * @param file - The file to validate
 * @param category - The file category (audio, video, image)
 * @returns True if file size is within limits
 */
export const validateFileSize = (file: File, category: keyof typeof SECURITY_CONFIG.MAX_FILE_SIZES): boolean => {
  return file.size <= SECURITY_CONFIG.MAX_FILE_SIZES[category];
};

/**
 * Validates input length against configured limits
 * @param input - The input string to validate
 * @param type - The input type (searchQuery, trackName, userContent, email)
 * @returns True if input length is within limits
 */
export const validateInputLength = (input: string, type: keyof typeof SECURITY_CONFIG.INPUT_LIMITS): boolean => {
  return input.length <= SECURITY_CONFIG.INPUT_LIMITS[type];
};

/**
 * Checks if the current environment is secure
 * @returns True if running in a secure context (HTTPS or localhost)
 */
export const isSecureContext = (): boolean => {
  return window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

/**
 * Generates a nonce for CSP
 * @returns A random nonce string
 */
export const generateNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Validates URL for security
 * @param url - The URL to validate
 * @returns True if URL is safe
 */
export const isValidUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    // Only allow http, https, and mailto protocols
    return ['http:', 'https:', 'mailto:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

/**
 * Sanitizes filename to prevent path traversal
 * @param filename - The filename to sanitize
 * @returns Sanitized filename
 */
export const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special characters with underscore
    .replace(/\.\./g, '') // Remove path traversal attempts
    .substring(0, 100); // Limit length
};